import React, { useEffect, useRef } from "react";

function getPlacePrediction(event) {
  return event.placePrediction ?? event.detail?.placePrediction;
}

function parseLatLng(location) {
  if (!location) return null;
  if (typeof location.lat === "function") {
    return { lat: location.lat(), lng: location.lng() };
  }
  return { lat: location.lat, lng: location.lng };
}

function extractCityCountry(addressComponents) {
  let city = "";
  let country = "";
  if (!addressComponents?.length) return { city, country };

  for (const comp of addressComponents) {
    const types = comp.types || [];
    if (types.includes("country")) {
      country = comp.longText || "";
    }
  }
  for (const comp of addressComponents) {
    const types = comp.types || [];
    if (types.includes("locality")) {
      city = comp.longText || "";
      break;
    }
  }
  if (!city) {
    for (const comp of addressComponents) {
      const types = comp.types || [];
      if (types.includes("administrative_area_level_1")) {
        city = comp.longText || "";
        break;
      }
    }
  }

  return { city, country };
}

const GoogleLocationAutocomplete = ({ onChange, error, value }) => {
  const containerRef = useRef(null);
  const autocompleteRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);
  onChangeRef.current = onChange;
  valueRef.current = value;

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !window.google?.maps) return undefined;

    let cancelled = false;
    let placeAutocomplete;
    let handler;

    (async () => {
      const g = window.google;
      await g.maps.importLibrary("places");
      if (cancelled || !containerRef.current) return;

      placeAutocomplete = new g.maps.places.PlaceAutocompleteElement({});

      placeAutocomplete.placeholder = "Search location";
      // Host-level styles (official API) — single box, no wrapper = no double borders
      placeAutocomplete.style.setProperty("color-scheme", "light");
      placeAutocomplete.style.setProperty("background-color", "#F7F9FA");
      placeAutocomplete.style.setProperty("border", "1px solid #d1d5db");
      placeAutocomplete.style.setProperty("border-radius", "0.5rem");
      placeAutocomplete.style.setProperty("width", "100%");
      placeAutocomplete.style.setProperty("box-sizing", "border-box");
      placeAutocomplete.style.setProperty("padding", "0");
      placeAutocomplete.style.setProperty("box-shadow", "none");

      containerRef.current.innerHTML = "";
      containerRef.current.appendChild(placeAutocomplete);
      autocompleteRef.current = placeAutocomplete;

      const initial = valueRef.current?.fullAddress;
      if (initial) {
        placeAutocomplete.value = initial;
      }

      handler = async (event) => {
        const placePrediction = getPlacePrediction(event);
        if (!placePrediction) return;

        const place = placePrediction.toPlace();
        await place.fetchFields({
          fields: ["id", "formattedAddress", "location", "addressComponents"],
        });

        const coords = parseLatLng(place.location);
        if (!coords) return;

        const { city, country } = extractCityCountry(place.addressComponents);

        const rawId = place.id || "";
        const placeId = rawId.startsWith("places/")
          ? rawId.slice("places/".length)
          : rawId;

        onChangeRef.current({
          placeId,
          fullAddress: place.formattedAddress || "",
          latitude: coords.lat,
          longitude: coords.lng,
          city,
          country,
        });
      };

      placeAutocomplete.addEventListener("gmp-select", handler);
    })();

    return () => {
      cancelled = true;
      if (placeAutocomplete && handler) {
        placeAutocomplete.removeEventListener("gmp-select", handler);
      }
      autocompleteRef.current = null;
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
    };
  }, []);

  useEffect(() => {
    const el = autocompleteRef.current;
    if (el && value?.fullAddress != null && value.fullAddress !== "") {
      el.value = value.fullAddress;
    }
  }, [value?.fullAddress]);

  return (
    <div className="w-full">
      <div
        ref={containerRef}
        className="google-place-autocomplete-host w-full [&_gmp-place-autocomplete]:block [&_gmp-place-autocomplete]:w-full"
      />
      {error && <p className="text-red-500 text-sm">{error}</p>}
    </div>
  );
};

export default GoogleLocationAutocomplete;
